import { AfterViewInit, Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { ProgressBagEvents } from './progressbag-events.service';
import { Observable } from 'rxjs';
import { TwoColumnLayoutComponent } from '@app/shared/components/liveops/two-column-layout/two-column-layout.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { AnimatedAssetComponent } from './animated-asset/animated-asset.component';
import { ProgressBagProgressBarComponent } from './progressbag-progress-bar/progressbag-progress-bar.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { ThemingService } from '@app/shared/services/theming.service';
import { EventsRepository } from '@app/shared/state/events/events.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-progressbag-modal',
  templateUrl: './progressbag.component.html',
  styleUrls: ['./progressbag.component.scss'],
  standalone: true,
  imports: [
    TwoColumnLayoutComponent,
    AnimatedTimerComponent,
    AnimatedAssetComponent,
    ProgressBagProgressBarComponent,
    ButtonComponent,
    SharedModule,
  ],
})
export class ProgressbagModalComponent implements OnInit, AfterViewInit {
  componentDestroy: () => Observable<unknown>;

  protected readonly progressbagModalData = inject<Modal>(MAT_DIALOG_DATA);
  private eventManager = inject(EventManager);
  public logger = inject(LoggerService);
  private eventsRepo = inject(EventsRepository);
  private progressBagService = inject(ProgressBagEvents);
  private translateService = inject(TranslateService);
  private themingService = inject(ThemingService);

  NonPersistedModalName = NonPersistedModalName;

  progressbag = signal<LimitedTimeEvent | null>(null);
  lang = signal<string>('');
  progressbagTreeStep = signal<number>(1);
  currentPrize = signal<number>(0);
  pointsDifference = signal<number>(0);
  maxReward = signal<number>(0);

  showAnimations = signal<boolean>(false);
  points = signal<number>(0);

  stepIcon = signal<{ activeAsset: string; inactiveAsset: string } | null>(null);
  finalIcon = signal<{ activeAsset: string; inactiveAsset: string } | null>(null);

  constructor() {
    this.showAnimations.set(this.progressbagModalData.data.animated);
  }

  ngOnInit(): void {
    const eventId = this.progressbagModalData.data.modal.eventId
      ? this.progressbagModalData.data.modal.eventId
      : this.progressbagModalData.data.modal.properties.eventId;

    const event = this.eventsRepo.getEventById(eventId);
    this.progressbag.set(event);
    this.lang.set(this.translateService.currentLang);

    this.setIcons();
    this.calculateCurrentPrize();
    this.calculateMaxPrize();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const evt = this.progressbag();
      if (evt) {
        this.points.set(evt.current);
      }
    });
  }

  setIcons() {
    this.stepIcon.set({
      activeAsset: this.setIcon('watering-on'),
      inactiveAsset: this.setIcon('watering-off'),
    });

    this.finalIcon.set({
      activeAsset: this.setIcon('tree-goal-on'),
      inactiveAsset: this.setIcon('tree-goal-off'),
    });
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(this.progressbagModalData.id, ModalType.View, this.progressbag()));
  }

  openShop(): void {
    this.closeModal();

    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ShopModalComponent, ModalType.View, {
        tab: 'left',
      })
    );
  }

  openInfo2(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ProgressbagInfo2ModalComponent, ModalType.View, this.progressbag())
    );
  }

  calculateMaxPrize() {
    const evt = this.progressbag();
    if (!evt) return;

    const multipliers = evt.properties.pointsperbagmultiplier.map((m) => m.bagMultiplier);

    this.maxReward.set(this.progressBagService.roundedInt(evt.properties.recurring * Math.max(...multipliers), 2));
  }

  calculateCurrentPrize(): void {
    const evt = this.progressbag();
    if (!evt) return;

    const completedRanges = evt.properties.pointsperbagmultiplier
      .filter((p) => evt.current >= p.points)
      .map((r) => r.points);

    const bagMultiplierFinded = evt.properties.pointsperbagmultiplier.find((r, i) => {
      if (r.points === Math.max(...completedRanges)) {
        this.progressbagTreeStep.set(i + 1);

        if (completedRanges.length < evt.properties.pointsperbagmultiplier.length) {
          this.pointsDifference.set(evt.properties.pointsperbagmultiplier[completedRanges.length].points - evt.current);
        } else {
          this.pointsDifference.set(0);
        }

        return r.bagMultiplier;
      }
      return false;
    });

    this.currentPrize.set(evt.properties.recurring * (bagMultiplierFinded?.bagMultiplier ?? 0));
  }

  private setIcon(iconPath: string): string {
    const evt = this.progressbag();

    if (!evt?.assets) {
      return this.themingService.getThemeAsset('progressivebag', iconPath);
    } else {
      return this.themingService.getOverrideThemeAsset(evt.assets, iconPath);
    }
  }
}
